import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';

export interface BlacklistPerson {
  id: number;
  fullName: string | null;
  email: string;
  phoneNumber: string | null;
  emailBlacklisted: boolean;
  emailBlacklistedAt: string | null;
  emailBlacklistReason: string | null;
  emailBounced: boolean;
  emailBouncedAt: string | null;
  emailSpamReported: boolean;
  emailUnsubscribed: boolean;
  place: {
    id: string;
    displayName: string | null;
    formattedAddress: string | null;
  } | null;
  createdAt: string;
}

export interface BlacklistStats {
  total: number;
  manual: number;
  bounced: number;
  spam: number;
  unsubscribed: number;
}

interface BlacklistResponse {
  items: BlacklistPerson[];
  total: number;
  stats: BlacklistStats;
}

export const useBlacklist = (search: string, filterType: string, offset: number) => {
  return useQuery<BlacklistResponse>({
    queryKey: ['blacklist', search, filterType, offset],
    queryFn: async () => {
      const params: Record<string, string | number> = { offset, limit: 20 };
      if (search) params.search = search;
      if (filterType !== 'all') params.type = filterType;
      const { data } = await api.get(API_ROUTES.emailBlacklist, { params });
      return data;
    },
  });
};

export const useAddToBlacklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { emails?: string[]; personIds?: number[]; reason?: string }) => {
      const { data } = await api.post(API_ROUTES.emailBlacklist, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
    },
  });
};

export const useRemoveFromBlacklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (personIds: number[]) => {
      const { data } = await api.delete(API_ROUTES.emailBlacklist, { data: { personIds } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
    },
  });
};
